/**
 * TikTokPostApprovalModal.jsx
 * 
 * Modal for approving individual TikTok posts (per-post consent).
 * Required by TikTok API guidelines - user must consent to privacy settings
 * before each post is published.
 * 
 * Usage:
 * <TikTokPostApprovalModal 
 *   post={postData}
 *   onApprove={() => refetchPosts()}
 *   onReject={() => refetchPosts()}
 *   onClose={() => setShowModal(false)}
 * />
 */

import { useState } from "react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { 
  X, 
  Check, 
  Eye, 
  EyeOff, 
  MessageCircle, 
  Users, 
  Scissors, 
  Tag,
  AlertTriangle,
  Calendar,
  Image as ImageIcon,
  Loader2
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const PRIVACY_OPTIONS = [
  { value: "PUBLIC_TO_EVERYONE", label: "Public", description: "Anyone can view", icon: Eye },
  { value: "MUTUAL_FOLLOW_FRIENDS", label: "Friends", description: "Only mutual followers", icon: Users },
  { value: "FOLLOWER_OF_CREATOR", label: "Followers", description: "Only your followers", icon: Users },
  { value: "SELF_ONLY", label: "Private", description: "Only you can view", icon: EyeOff }
];

export default function TikTokPostApprovalModal({ post, onApprove, onReject, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState({
    privacy_level: "",
    allow_comment: true,
    allow_duet: false,
    allow_stitch: false,
    disclosure_enabled: false,
    is_your_brand: false,
    is_branded_content: false
  });

  const handleApprove = async () => {
    if (!settings.privacy_level) {
      toast.error("Please select a privacy level");
      return;
    }

    if (settings.is_branded_content && settings.privacy_level === "SELF_ONLY") {
      toast.error("Branded content cannot be set to Private");
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post("/social/tiktok/approve-post", {
        post_id: post.id,
        ...settings
      });
      
      toast.success("Post approved for TikTok!");
      onApprove?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve post");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    try {
      await apiClient.post(`/social/tiktok/reject-post/${post.id}`);
      toast.info("TikTok removed from this post");
      onReject?.();
    } catch (error) {
      toast.error("Failed to update post");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-lg bg-viraly-card border border-white/10 rounded-2xl overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-5 border-b border-white/10 bg-gradient-to-r from-black/40 to-transparent">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center">
                  <span className="text-sm font-bold text-white">TT</span>
                </div>
                <div>
                  <h2 className="text-lg font-semibold">TikTok Post Approval</h2>
                  <p className="text-sm text-viraly-muted">Per TikTok guidelines, you must approve each post</p>
                </div>
              </div>
              <button onClick={onClose} className="text-viraly-muted hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Post Preview */}
          <div className="p-4 border-b border-white/10 bg-black/20">
            <div className="flex gap-4">
              {post.media_url && (
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-black/30 flex-shrink-0">
                  <img src={post.media_url} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm line-clamp-2">{post.content || post.caption}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-viraly-muted">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(post.scheduled_at).toLocaleDateString()}
                  </span>
                  {post.media_type && (
                    <span className="flex items-center gap-1">
                      <ImageIcon className="w-3 h-3" />
                      {post.media_type}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Settings Form */}
          <div className="p-5 space-y-5 max-h-[400px] overflow-y-auto">
            {/* Privacy Level - Required */}
            <div>
              <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                <Eye className="w-4 h-4 text-viraly-orange" />
                Privacy Level <span className="text-red-400">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {PRIVACY_OPTIONS.map(option => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      onClick={() => setSettings(s => ({ ...s, privacy_level: option.value }))}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        settings.privacy_level === option.value
                          ? "border-viraly-orange bg-viraly-orange/10"
                          : "border-white/10 hover:border-white/20"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        <span className="font-medium text-sm">{option.label}</span>
                      </div>
                      <p className="text-xs text-viraly-muted mt-1">{option.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Interaction Settings */}
            <div>
              <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-blue-400" />
                Interaction Settings
              </label>
              <div className="space-y-2">
                <label className="flex items-center justify-between p-3 bg-black/20 rounded-lg cursor-pointer hover:bg-black/30">
                  <span className="text-sm">Allow Comments</span>
                  <input
                    type="checkbox"
                    checked={settings.allow_comment}
                    onChange={e => setSettings(s => ({ ...s, allow_comment: e.target.checked }))}
                    className="w-4 h-4 rounded text-viraly-orange"
                  />
                </label>
                <label className="flex items-center justify-between p-3 bg-black/20 rounded-lg cursor-pointer hover:bg-black/30">
                  <span className="text-sm">Allow Duets</span>
                  <input
                    type="checkbox"
                    checked={settings.allow_duet}
                    onChange={e => setSettings(s => ({ ...s, allow_duet: e.target.checked }))}
                    className="w-4 h-4 rounded text-viraly-orange"
                  />
                </label>
                <label className="flex items-center justify-between p-3 bg-black/20 rounded-lg cursor-pointer hover:bg-black/30">
                  <span className="text-sm">Allow Stitches</span>
                  <input
                    type="checkbox"
                    checked={settings.allow_stitch}
                    onChange={e => setSettings(s => ({ ...s, allow_stitch: e.target.checked }))}
                    className="w-4 h-4 rounded text-viraly-orange"
                  />
                </label>
              </div>
            </div>

            {/* Commercial Disclosure */}
            <div>
              <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                <Tag className="w-4 h-4 text-green-400" />
                Commercial Content Disclosure
              </label>
              <label className="flex items-center justify-between p-3 bg-black/20 rounded-lg cursor-pointer hover:bg-black/30 mb-2">
                <div>
                  <span className="text-sm">Enable Disclosure</span>
                  <p className="text-xs text-viraly-muted">Required if promoting products/services</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.disclosure_enabled}
                  onChange={e => setSettings(s => ({ 
                    ...s, 
                    disclosure_enabled: e.target.checked,
                    is_your_brand: e.target.checked ? s.is_your_brand : false,
                    is_branded_content: e.target.checked ? s.is_branded_content : false
                  }))}
                  className="w-4 h-4 rounded text-viraly-orange"
                />
              </label>
              
              {settings.disclosure_enabled && (
                <div className="ml-4 space-y-2 pt-2 border-t border-white/10">
                  <label className="flex items-center gap-2 p-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.is_your_brand}
                      onChange={e => setSettings(s => ({ ...s, is_your_brand: e.target.checked }))}
                      className="w-4 h-4 rounded text-viraly-orange"
                    />
                    <span className="text-sm">Your Brand</span>
                  </label>
                  <label className="flex items-center gap-2 p-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.is_branded_content}
                      onChange={e => setSettings(s => ({ ...s, is_branded_content: e.target.checked }))}
                      className="w-4 h-4 rounded text-viraly-orange"
                    />
                    <span className="text-sm">Branded Content (Sponsored)</span>
                  </label>
                </div>
              )}
            </div>

            {/* Warning for branded content + private */}
            {settings.is_branded_content && settings.privacy_level === "SELF_ONLY" && (
              <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                Branded content cannot be set to Private visibility
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="p-5 border-t border-white/10 bg-black/20 flex gap-3">
            <Button
              onClick={handleReject}
              variant="outline"
              className="flex-1"
              disabled={submitting}
            >
              Skip TikTok
            </Button>
            <Button
              onClick={handleApprove}
              className="flex-1 bg-viraly-orange hover:bg-viraly-orange/90"
              disabled={submitting || !settings.privacy_level}
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Approving...</>
              ) : (
                <><Check className="w-4 h-4 mr-2" /> Approve Post</>
              )}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
